import { addMealItem, AddMealItemPayload } from "@/packages/dashboard/api/add-meal-item";
import { deleteMealItem } from "@/packages/dashboard/api/delete-meal-item";
import { invalidateMealsQueries } from "@/packages/dashboard/query-client";
import { toIsoDateTime } from "@/packages/dashboard/utils/date-range-helpers";

import { useCallback, useMemo, useState } from "react";
import type { FormEvent } from "react";

import type { FoodItem } from "./food-item-combobox";

// Actions in this hook return the identifier of the field that should receive
// focus next so the companion focus hook can move the cursor at the right time.
export type FocusTarget = "food" | "quantity";

export type MealEntry = {
  clientId: string;
  id: number | string | null;
  foodItem: FoodItem;
  quantity: number;
};

export type MealFormState = {
  foodItem: FoodItem | null;
  quantity: number | null;
};

const createEmptyFormState = (): MealFormState => ({
  foodItem: null,
  quantity: null,
});

const generateClientId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export type UseMealFormResult = {
  formState: MealFormState;
  mealItems: MealEntry[];
  isSubmitDisabled: boolean;
  // Each action resolves to a focus target, allowing the UI to hand that
  // request to useMealFocus so the cursor follows the user flow.
  submit: (event: FormEvent<HTMLFormElement>) => FocusTarget | null;
  changeFoodItem: (value: FoodItem | null) => FocusTarget | null;
  changeQuantity: (value: number | string | null | undefined) => void;
  deleteEntry: (entry: MealEntry) => void;
  prepareForNewFoodDraft: () => void;
  applyNewFoodItem: (item: FoodItem) => FocusTarget;
  replaceMealEntries: (entries: MealEntry[]) => void;
};

type UseMealFormParams = {
  mealTimestamp: Date | null;
};

export const useMealForm = ({ mealTimestamp }: UseMealFormParams): UseMealFormResult => {
  const [mealItems, setMealItems] = useState<MealEntry[]>([]);
  const [formState, setFormState] = useState<MealFormState>(createEmptyFormState);

  const changeFoodItem = useCallback((value: FoodItem | null): FocusTarget | null => {
    setFormState((current) => ({
      ...current,
      foodItem: value,
      quantity: value?.serving_size ?? null,
    }));

    if (!value) {
      return "food";
    }

    return "quantity";
  }, []);

  const changeQuantity = useCallback((value: number | string | null | undefined) => {
    setFormState((current) => ({
      ...current,
      quantity:
        value === "" || value === null || value === undefined ? null : typeof value === "number" ? value : Number(value),
    }));
  }, []);

  const prepareForNewFoodDraft = useCallback(() => {
    setFormState((current) => ({
      ...current,
      foodItem: null,
      quantity: null,
    }));
  }, []);

  const applyNewFoodItem = useCallback((item: FoodItem): FocusTarget => {
    setFormState((current) => ({
      ...current,
      foodItem: item,
      quantity: item.serving_size,
    }));
    return "quantity";
  }, []);

  const replaceMealEntries = useCallback((entries: MealEntry[]) => {
    setMealItems(entries);
    setFormState(createEmptyFormState());
  }, []);

  const deleteEntry = useCallback((entry: MealEntry) => {
    let removedIndex = -1;

    setMealItems((current) => {
      const index = current.findIndex((item) => item.clientId === entry.clientId);
      if (index === -1) {
        return current;
      }

      removedIndex = index;
      return [...current.slice(0, index), ...current.slice(index + 1)];
    });

    if (removedIndex === -1) {
      return;
    }

    const numericId = typeof entry.id === "number" ? entry.id : entry.id ? Number(entry.id) : NaN;
    if (!Number.isFinite(numericId) || numericId <= 0) {
      return;
    }

    void (async () => {
      try {
        await deleteMealItem(Number(numericId));
      } catch (error) {
        console.error("Failed to delete meal item", error);
        setMealItems((current) => {
          const exists = current.some((item) => item.clientId === entry.clientId);
          if (exists) {
            return current;
          }

          const next = [...current];
          const insertIndex = Math.min(removedIndex, next.length);
          next.splice(insertIndex, 0, entry);
          return next;
        });
      }
    })();
  }, []);

  const isSubmitDisabled = useMemo(() => {
    if (!formState.foodItem) {
      return true;
    }

    if (!formState.quantity) {
      return true;
    }

    return formState.quantity <= 0;
  }, [formState.foodItem, formState.quantity]);

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>): FocusTarget | null => {
      event.preventDefault();

      const { foodItem, quantity } = formState;

      if (!foodItem || !quantity) {
        return null;
      }

      const mealTimestampIso = toIsoDateTime(mealTimestamp);
      if (!mealTimestampIso) {
        console.error("Cannot save meal item without a valid meal timestamp");
        return null;
      }
      const nextClientId = generateClientId();
      const optimisticEntry: MealEntry = {
        clientId: nextClientId,
        id: null,
        foodItem,
        quantity,
      };

      setMealItems((current) => [...current, optimisticEntry]);

      setFormState(createEmptyFormState());

      const mealItemPayload: AddMealItemPayload["meal"]["meal_items"][number] = {
        food_item_id: foodItem.id,
        quantity,
      };

      const payload: AddMealItemPayload = {
        meal: {
          timestamp: mealTimestampIso,
          meal_items: [
            mealItemPayload,
          ],
        },
      };

      void (async () => {
        try {
          const data = await addMealItem(payload);
          const persistedEntry: MealEntry = {
            clientId: nextClientId,
            id: data.meal_item.id,
            foodItem,
            quantity: data.meal_item.quantity,
          };

          setMealItems((current) =>
            current.map((entry) => (entry.clientId === nextClientId ? persistedEntry : entry)),
          );

          invalidateMealsQueries();
        } catch (error) {
          console.error("Failed to add meal item", error);

          setMealItems((current) => {
            const entryExists = current.some((entry) => entry.clientId === nextClientId);
            if (!entryExists) {
              return current;
            }

            return current.filter((entry) => entry.clientId !== nextClientId);
          });
        }
      })();

      return "food";
    },
    [formState, mealTimestamp],
  );

  return {
    formState,
    mealItems,
    isSubmitDisabled,
    submit,
    changeFoodItem,
    changeQuantity,
    deleteEntry,
    prepareForNewFoodDraft,
    applyNewFoodItem,
    replaceMealEntries,
  };
};
